// STEP OMS-PARTIAL-PICK-1 · 다상품 주문에서 재고 있는 상품만 선택 피킹.
// - GET /seller/fulfillment/{sid}/items 로 per-item 상태 로드 (REUSE).
// - POST /seller/fulfillment/{sid}/pick with body {item_refs:[...]} (신규 확장).
// - 미선택 아이템 중 미출고 상품은 backend 가 waiting_stock 으로 안전 마킹.
// - PartialShipmentModal · FulfillmentSection 등 기존 UI 는 무손상.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X as XIcon, Loader2, Package, Play } from 'lucide-react';
import api from '@/lib/api';

const ITEM_STATUS_LABEL = {
  ready:         '출고준비',
  waiting_stock: '입고대기',
  hold:          '출고보류',
  out_of_stock:  '품절',
  cancelled:     '취소',
  shipped:       '출고완료',
};
const ITEM_STATUS_CLS = {
  ready:         'bg-emerald-100 text-emerald-800',
  waiting_stock: 'bg-amber-100 text-amber-800',
  hold:          'bg-indigo-100 text-indigo-800',
  out_of_stock:  'bg-red-100 text-red-800',
  cancelled:     'bg-gray-200 text-gray-700',
  shipped:       'bg-blue-100 text-blue-800',
};

const _renderOption = (opt) => {
  if (opt && typeof opt === 'object') {
    return Object.entries(opt).filter(([, v]) => v !== null && v !== '')
      .map(([, v]) => v).join(' / ');
  }
  return typeof opt === 'string' ? opt : '';
};

export default function SelectivePickModal({
  open, sellerOrderId, orderNumber, onClose, onPicked,
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [picks, setPicks] = useState(new Set());   // Set<seller_order_item_id>
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !sellerOrderId) return;
    setPicks(new Set());
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/seller/fulfillment/${sellerOrderId}/items`);
        const its = Array.isArray(r.data?.items) ? r.data.items : [];
        setItems(its);
        setSummary(r.data?.summary || {});
        // 기본: ready 아이템 전체 pre-select (편의)
        const defaultPicks = new Set(
          its.filter((it) => it.fulfillment_status === 'ready' && (it.pending_qty || 0) > 0)
             .map((it) => it.seller_order_item_id)
             .filter(Boolean)
        );
        setPicks(defaultPicks);
      } catch (e) {
        toast.error(e?.response?.data?.detail || '주문 상품 조회 실패');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, sellerOrderId]);

  if (!open) return null;

  const pickableItems = items.filter(
    (it) => it.fulfillment_status === 'ready' && (it.pending_qty || 0) > 0 && it.seller_order_item_id
  );
  const nonPickableItems = items.filter((it) => !pickableItems.includes(it));

  const toggle = (iid) => {
    setPicks((prev) => {
      const next = new Set(prev);
      if (next.has(iid)) next.delete(iid); else next.add(iid);
      return next;
    });
  };
  const toggleAll = () => {
    const allSel = pickableItems.every((it) => picks.has(it.seller_order_item_id));
    setPicks(allSel ? new Set() : new Set(pickableItems.map((it) => it.seller_order_item_id)));
  };

  const unSelectedPickable = pickableItems.filter((it) => !picks.has(it.seller_order_item_id));
  // STEP OMS-PARTIAL-PICK-2A-FIX · "후출고" = 재고 있음 · 이번 pick 미포함
  //   waiting_stock 로 위장하지 않는다 (③ 재고확인 카운트 오염 금지).
  const deferredCount = unSelectedPickable.length;

  const submit = async () => {
    if (picks.size === 0) {
      toast.error('피킹할 상품을 최소 1개 선택해주세요.');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/seller/fulfillment/${sellerOrderId}/pick`,
        { item_refs: Array.from(picks) });
      toast.success(`선택 상품 ${picks.size}개 피킹 시작 · ⑤ 출고관리`);
      // STEP OMS-PARTIAL-PICK-2A-FIX · Pass pick response back for auto-open scan drawer.
      onPicked?.(r?.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '피킹 시작 실패');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="selective-pick-overlay">
      <div className="bg-white dark:bg-[#141414] rounded-xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-black/10 dark:border-white/10"
           onClick={(e) => e.stopPropagation()} data-testid="selective-pick-modal">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
          <div className="min-w-0">
            <h3 className="text-base font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
              선택 상품 피킹 시작
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              주문번호 <b className="font-mono">{orderNumber || sellerOrderId}</b>
              {summary?.label && <span className="ml-2 text-gray-400">· {summary.label}</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-[#FF3B30]"
                  aria-label="닫기" data-testid="selective-pick-close">
            <XIcon size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2" data-testid="selective-pick-body">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-10">
              <Loader2 size={16} className="animate-spin" /> 주문 상품 불러오는 중…
            </div>
          ) : items.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-10">주문 상품이 없습니다.</p>
          ) : (
            <>
              {pickableItems.length > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500 pb-1">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input type="checkbox"
                           checked={pickableItems.length > 0 && pickableItems.every((it) => picks.has(it.seller_order_item_id))}
                           onChange={toggleAll}
                           className="accent-[#FF3B30]"
                           data-testid="selective-pick-select-all" />
                    <b>전체 선택</b> ({picks.size}/{pickableItems.length})
                  </label>
                  <span>재고 있는 상품만 선택 가능</span>
                </div>
              )}
              {items.map((it, i) => {
                const iid = it.seller_order_item_id;
                const isPickable = pickableItems.includes(it);
                const isPicked = iid && picks.has(iid);
                const fs = it.fulfillment_status || 'ready';
                const opt = _renderOption(it.option);
                const qty = it.pending_qty || it.quantity || 0;
                return (
                  <label key={iid || i}
                         className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition ${
                           isPicked ? 'border-[#FF3B30]/60 bg-[#FF3B30]/5'
                                    : isPickable ? 'border-black/10 dark:border-white/10 hover:border-[#FF3B30]/30 cursor-pointer'
                                                 : 'border-black/5 dark:border-white/5 bg-black/[0.03] dark:bg-white/[0.03] cursor-not-allowed opacity-80'
                         }`}
                         data-testid={`selective-pick-item-${iid || i}`}>
                    <input type="checkbox"
                           checked={isPicked}
                           disabled={!isPickable}
                           onChange={() => iid && toggle(iid)}
                           className="accent-[#FF3B30]"
                           data-testid={`selective-pick-check-${iid || i}`} />
                    <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-black/5 dark:bg-white/5 flex items-center justify-center">
                      {(it.image_snapshot || it.image)
                        ? <img src={it.image_snapshot || it.image} alt="" className="w-full h-full object-cover" loading="lazy"
                               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        : <Package size={14} className="text-gray-300" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate"
                         data-testid={`selective-pick-name-${iid || i}`}>
                        {it.name_snapshot || it.product_name_snapshot || it.product_name || '-'}
                        {opt && <span className="text-gray-400 font-normal"> · {opt}</span>}
                      </p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-1.5 mt-0.5">
                        <span>수량 × {qty}</span>
                        <span className={`px-1.5 py-0.5 rounded-full font-bold ${ITEM_STATUS_CLS[fs] || 'bg-gray-100 text-gray-700'}`}
                              data-testid={`selective-pick-status-${iid || i}`}>
                          {ITEM_STATUS_LABEL[fs] || fs}
                        </span>
                        {it.eta_date && (
                          <span className="text-gray-400">입고예정 {it.eta_date}</span>
                        )}
                      </p>
                    </div>
                  </label>
                );
              })}
              {deferredCount > 0 && (
                <div className="mt-2 p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-300/60 text-[11px] text-indigo-800 dark:text-indigo-200"
                     data-testid="selective-pick-waiting-hint">
                  선택하지 않은 상품 <b className="tabular-nums">{deferredCount}</b>개는 이번 출고에서
                  제외 (<b className="mx-0.5">후출고</b>) — 재고는 유지되고 추후 별도 피킹 가능합니다.
                  <span className="ml-1 text-indigo-600/70 dark:text-indigo-300/70">입고대기(재고부족)와 구분됩니다.</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-black/10 dark:border-white/10 flex items-center justify-between gap-2"
             data-testid="selective-pick-footer">
          <p className="text-[11px] text-gray-500">
            선택 <b className="text-black dark:text-white tabular-nums">{picks.size}</b>개 ·
            후출고 <b className="text-indigo-600 tabular-nums">{deferredCount}</b>개
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
                    className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold disabled:opacity-50"
                    data-testid="selective-pick-cancel">
              취소
            </button>
            <button onClick={submit}
                    disabled={busy || loading || picks.size === 0}
                    className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-[#FF3B30] text-white text-xs font-black disabled:opacity-40"
                    data-testid="selective-pick-submit">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              선택 상품 피킹 시작
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
